import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import "./PaymentTable.css";
import { FilterAltOutlinedIcon } from "../../components/icons/Icons";
import PaymentModal from "./PaymentModal";
import BankModal from "../../components/bank/Bank";

interface Payment {
  id: string;
  date: string;
  orderId: string;
  customerName: string;
  amount: string;
  phone: string;
}

const PaymentTable: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);

  // Fetch transactions (server-side pagination)
  const fetchTransactions = async (page = currentPage, limit = entriesPerPage) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/transactions?page=${page}&limit=${limit}`
      );

      if (res.data?.valid) {
        const data = res.data.data;

        const txs: Payment[] = (data.transactions || []).map((tx: any) => ({
          id: tx.id,
          date: new Date(tx.createdAt).toLocaleDateString(),
          orderId: tx.reference || tx.order?.order_code || "-",
          customerName: tx.payer_name || tx.user?.full_name || "-",
          amount: tx.amount,
          phone: tx.user?.phone_number || "-",
        }));

        setPayments(txs);
        setTotalPayments(data.pagination?.total || 0);
      } else {
        setPayments([]);
        setTotalPayments(0);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when page or limit changes
  useEffect(() => {
    fetchTransactions(currentPage, entriesPerPage);
  }, [currentPage, entriesPerPage]);

  const handleFilter = (filters: any) => {
    console.log("Filters Applied:", filters);
  };

  const totalPages = Math.ceil(totalPayments / entriesPerPage);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleEntriesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="payment-table">
      <div className="payment__table__header">
        <h3>All Payments</h3>
        <div className="payment__table__header__actions">
          <button onClick={() => setFilterModalOpen(true)}>
            <FilterAltOutlinedIcon />
            <p>Filter</p>
          </button>

          <button onClick={() => setAddModalOpen(true)}>
            <p>Add Payment Info</p>
          </button>
        </div>
      </div>

      <div className="table-s">
        <table>
          <thead>
            <tr>
              <th>PAYMENT ID</th>
              <th>PAYMENT NAME</th>
              <th>PAYMENT DATE</th>
              <th>PHONE NUMBER</th>
              <th>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  No transactions found.
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.orderId}</td>
                  <td>{payment.customerName}</td>
                  <td>{payment.date}</td>
                  <td>{payment.phone}</td>
                  <td>{payment.amount}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Entries dropdown and pagination info */}
      <div className="entries-control">
        <label>
          View
          <select
            className="entries-select"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            <option value="2">2</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </select>
          Per Page
        </label>

        <div className="right__pagination">
          <p>
            Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
            {Math.min(currentPage * entriesPerPage, totalPayments)} of {totalPayments} entries
          </p>

          <div className="pagination">
            <button
              className="pagination__button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`pagination__button ${
                  currentPage === page ? "active" : ""
                }`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="pagination__button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <PaymentModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilter={handleFilter}
      />

      <BankModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        bankDetails={{
          accountName: "John Doe",
          accountNumber: "123456789",
          routingNumber: "021000021",
          bankName: "Chase Bank",
          bankAddress: "270 Park Ave, New York, NY 10017",
          swiftCode: "CHASUS33",
        }}
      />
    </div>
  );
};

export default PaymentTable;